import { collection, query, where, getDocs } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { db } from '../../config/firebaseConfig';
import { isPatientOwner, normalizePatientId } from '../../utils/patientAccess';
import { PATIENT_STATUSES, buildPatientSummary, formatDate, formatRate } from './projectHelpers';

// Firestore "in" sorgusu tek seferde en fazla 30 değer kabul ediyor
const IN_QUERY_LIMIT = 30;

const chunk = (items, size) => {
  const result = [];
  for (let i = 0; i < items.length; i += size) {
    result.push(items.slice(i, i + size));
  }
  return result;
};

const unique = (values) => [...new Set(values.filter(Boolean))].join(', ') || '-';

/**
 * Downloads the employee's project report (one row per project, with their
 * own patients and visits). The workbook stays Turkish whatever the UI
 * language: it is a shared document, and its columns are read by people.
 */
export const downloadEmployeeProjectsReport = async ({ rows, employee, isAdmin }) => {
  const projectIds = rows.map(row => row.project.id);
  const patientsByProject = new Map();
  const visitsByProject = new Map();

  await Promise.all(
    chunk(projectIds, IN_QUERY_LIMIT).map(async (ids) => {
      const [patientsSnap, visitsSnap] = await Promise.all([
        getDocs(query(collection(db, 'Patients'), where('projectId', 'in', ids))),
        getDocs(query(collection(db, 'Visits'), where('projectId', 'in', ids))),
      ]);

      patientsSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        const list = patientsByProject.get(data.projectId) || [];
        list.push(data);
        patientsByProject.set(data.projectId, list);
      });

      visitsSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        const list = visitsByProject.get(data.projectId) || [];
        list.push(data);
        visitsByProject.set(data.projectId, list);
      });
    })
  );

  const excelRows = rows.map(({ project, assignments, assignmentStatus, projectStatus }) => {
    // Çıktı bu çalışanın raporu: proje geneli değil, çalışanın kendi
    // hastaları (sahip olduğu + ziyaret ettiği) ve kendi ziyaretleri.
    const visits = (visitsByProject.get(project.id) || []).filter(v => v.employeeId === employee.id);
    const visitedPatientIds = new Set(visits.map(v => normalizePatientId(v.patientId)).filter(Boolean));
    const employeePatients = (patientsByProject.get(project.id) || []).filter(
      patient =>
        isPatientOwner(patient, employee.id) ||
        visitedPatientIds.has(normalizePatientId(patient.patientId))
    );
    const patientSummary = buildPatientSummary(employeePatients);

    const row = {
      'Proje Kodu': project.titleShort || '-',
      'Protokol Numarası': project.protocolNumber || '-',
      'Müşteri': project.customer || '-',
      'Proje Adı': project.titleLong || '-',
      'Proje Tarihi': formatDate(project.projectDate),
      'Proje Bitiş Tarihi': formatDate(project.projectDueDate),
      'Proje Durumu': projectStatus,
      'Görev Durumu':
        assignmentStatus === 'active' ? 'Aktif' : assignmentStatus === 'passive' ? 'Pasif' : '-',
      'Merkezler': unique(assignments.map(a => a.centerName)),
      'Roller': unique(assignments.map(a => a.role)),
      'Çalışan Tipi': unique(assignments.map(a => a.workerType)),
      'Körleme Durumu': unique(assignments.map(a => a.blindStatus)),
      'Atanma Tarihi': assignments.length ? formatDate(assignments[0].assignedAt) : '-',
    };

    if (isAdmin) {
      row['Ücret / FTE'] = unique(assignments.map(a => formatRate(a.rate)));
    }

    row['Çalışanın Hastası'] = patientSummary.total;
    row['Ev Ziyaretli Hasta'] = patientSummary.homeVisit;
    Object.entries(PATIENT_STATUSES).forEach(([key, meta]) => {
      row[`Hasta - ${meta.label}`] = patientSummary.byStatus[key] || 0;
    });
    row['Çalışanın Ziyareti'] = visits.length;

    return row;
  });

  const worksheet = XLSX.utils.json_to_sheet(excelRows);
  worksheet['!cols'] = Object.keys(excelRows[0]).map(key => ({
    wch: Math.min(Math.max(key.length + 2, 12), 45),
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Projeler');

  const employeeName = `${employee.eName || ''}_${employee.eSurname || ''}`.trim().replace(/\s+/g, '_');
  const today = new Date().toISOString().split('T')[0];
  XLSX.writeFile(workbook, `Projeler_${employeeName || 'Calisan'}_${today}.xlsx`);
};
